<app-tool-loader [isLoading]="isLoading()" [positionUnset]="true">
  <form [formGroup]="form.formGroup">

    <div class="pb-5 field">
      <div class="flex items-center">
        <p-checkbox
          [formControlName]="form.formControlNames.useMudPits"
          inputId="useMudPits"
          binary="true"
          (onChange)="useMudPits = $event.checked;"
        ></p-checkbox>
        <label for="useMudPits" class="ml-2">Circulate through Mud Pits</label>
      </div>
    </div>

    <p-tabs value="0" (valueChange)="selectedTabChange.emit($event)">

      <p-tablist>
        <p-tab value="0">Operation Details</p-tab>
        @if (showDrillPipeData) {
          <p-tab value="1">
            Landing String
            @if (landingStringMissing) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Landing string not specified."></i>
            }
          </p-tab>
        }
        @if (showRiser) {
          <p-tab value="2">
            Riser
            @if (form.formGroup.controls?.insideDiameter.invalid || form.formGroup.controls?.outsideDiameter.invalid) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Operation not saved, some required inputs not specified."></i>
            }
          </p-tab>
        }
        @if (useMudPits) {
          <p-tab value="3">Mud Pits
            @if (form.formGroup.controls.mudPits.invalid) {
              <i class="validation-error pi pi-exclamation-circle pl-1" pTooltip="Operation not saved, double check Mud Pit inputs."></i>
            }
          </p-tab>
        }
        <p-tab value="4">
          Other
        </p-tab>
      </p-tablist>

      <p-tabpanels class="pl-4 pr-2">
        <p-tabpanel value="0">
          <div class="flex flex-wrap gap-4">
            <!-- Left Column -->
            <div class="flex flex-col flex-1 min-w-[150px] space-y-2">

              <div class="flex flex-col">
                <label for="tripIntoHole">Trip Into Hole</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.tripIntoHole" />
                  <p-inputgroup-addon>hr</p-inputgroup-addon>
                </p-inputgroup>
                @if (form.formGroup.controls.tripIntoHole.value && form.formGroup.controls.tripIntoHole.invalid) {
                  <p class="validation-error">Allowed range: 0 - 100000 hrs</p>
                }
              </div>

              <app-standard-fluid [formControlName]="form.formControlNames.standardFluidFlow"
                [operationType]="operationType" [userUnits]="userUnits">
              </app-standard-fluid>

              <div class="flex flex-col">
                <label for="startDrillingMd">Circulation Time</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.circulationTime" />
                  <p-inputgroup-addon>hr</p-inputgroup-addon>
                </p-inputgroup>
                @if (form.formGroup.controls.circulationTime.value && form.formGroup.controls.circulationTime.invalid) {
                  <p class="validation-error">Allowed range: 0 - 100000 hrs</p>
                }
              </div>

              <div class="flex flex-col">
                <label for="endDrillingMd">Static Period</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.staticPeriod" />
                  <p-inputgroup-addon>hr</p-inputgroup-addon>
                </p-inputgroup>
                @if (form.formGroup.controls.staticPeriod.value && form.formGroup.controls.staticPeriod.invalid) {
                  <p class="validation-error">Allowed range: 0 - 300 hrs</p>
                }
              </div>
              @if (isSubsea && currentTubularIndex > 0) {
              <div class="pt-3">
                <p-checkbox
                    size="small" 
                    [formControlName]="form.formControlNames.includeRiser"
                    inputId="includeRiser"
                    binary="true"
                    (onChange)="hasRiserChange();"
                  ></p-checkbox>
                  <label class="pl-2" for="includeRiser">Include Riser</label>
              </div>
              }
            </div>


            <div class="flex flex-col flex-1 min-w-[150px] space-y-2">
              <div class="flex flex-col">
                <label for="fluids">Fluid</label>
                <p-select filter filterBy="state.name" resetFilterOnHide="true" [disabled]="isReadOnly" [options]="fluids" [formControlName]="form.formControlNames.fluid"
                  dataKey="id" appendTo="body" optionLabel="state.name" tooltipPosition="top" 
                  >
                  <ng-template pTemplate="selectedItem">
                    {{ form.formGroup.value.fluid?.state.name }}
                  </ng-template>
                  <ng-template let-fluid pTemplate="item">
                    {{ fluid.state.name }}
                  </ng-template>
                </p-select>
              </div>

              <div class="flex flex-col">
                <label for="inletTemperature">Inlet Temperature</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.inletTemperature" />
                  <p-inputgroup-addon>{{ tempUnit }}</p-inputgroup-addon>
                </p-inputgroup>
                @if (form.formGroup.controls.inletTemperature.invalid) {
                  <p class="validation-error">Allowed range: {{temperatureValidation.min }} - {{ temperatureValidation.max }} {{ tempUnit }}</p>
                }
              </div>

            </div>
          </div>
        </p-tabpanel>

        <p-tabpanel value="1">
          <label class="{{ landingStringMissing ? 'validation-error' : '' }}">{{selectedLandingString}}</label>
        </p-tabpanel>

        <p-tabpanel value="2">
          <p-fieldset legend="Riser">
            <div class="space-y-2">
              <div class="flex flex-col">
                <label for="outsideDiameter">Outside Diameter</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.outsideDiameter" />
                  <p-inputgroup-addon>{{ pipeRoughnessUnit }}</p-inputgroup-addon>
                </p-inputgroup>
              </div>

              <div class="flex flex-col">
                <label for="outsideDiameter">Inside Diameter</label>
                <p-inputgroup>
                  <p-inputnumber [minFractionDigits]="1" [formControlName]="form.formControlNames.insideDiameter" />
                  <p-inputgroup-addon>{{ pipeRoughnessUnit }}</p-inputgroup-addon>
                </p-inputgroup>
              </div>
            </div>
          </p-fieldset>
        </p-tabpanel>

        <p-tabpanel value="3">
          @if (useMudPits) {
            <app-mud-pit-details
              [disableMudPits]="!useMudPits"
              [previousOperationMudPits]="previousOperationMudPits"
              [userUnits]="userUnits"
              [formControlName]="form.formControlNames.mudPits"
              [surfaceTemperature]="surfaceTemperature"
              [triggerMudPitTempChange]="triggerMudPitTempChange"
            ></app-mud-pit-details>
          }
        </p-tabpanel>

        <p-tabpanel value="4">
          <div class="flex flex-col">
            <label for="pipeRoughness">Pipe Roughness</label>
            <p-inputgroup>
              <p-inputnumber [minFractionDigits]="1" [maxFractionDigits]="6" [formControlName]="form.formControlNames.pipeRoughness" />
              <p-inputgroup-addon>{{ pipeRoughnessUnit }}</p-inputgroup-addon>
            </p-inputgroup>
              @if (form.formGroup.controls.pipeRoughness.invalid) {
                <p class="validation-error">Allowed range: {{ pipeRoughnessValidation.min }} - {{ pipeRoughnessValidation.max }} {{ pipeRoughnessUnit }}</p>
              }
          </div>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>

  </form>
</app-tool-loader>